import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, Bell, BellOff, Clock, Send,
  AlertTriangle, Sun, Sunset, Moon, ShieldAlert, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { formatError, notificationsApi } from "../lib/api";
import { PushEnableCard } from "../components/PushEnableCard";

const DEFAULT_PREFS = {
  enabled: true,
  expire_24h: true,
  expire_48h: true,
  expire_72h: false,
  expire_96h: false,
  custom_reminder: false,
  custom_reminder_days: [],
  custom_reminder_times: [],
};

const EXPIRATION_ROWS = [
  { key: "expire_24h", label: "24 hours", hint: "1 day before its best-by date" },
  { key: "expire_48h", label: "48 hours", hint: "2 days before its best-by date" },
  { key: "expire_72h", label: "72 hours", hint: "3 days before its best-by date" },
  { key: "expire_96h", label: "96 hours", hint: "4 days before its best-by date" },
];

// Sunday-first to match the user's spec.
const WEEKDAYS = [
  { key: 0, label: "Sun" },
  { key: 1, label: "Mon" },
  { key: 2, label: "Tue" },
  { key: 3, label: "Wed" },
  { key: 4, label: "Thu" },
  { key: 5, label: "Fri" },
  { key: 6, label: "Sat" },
];

const TIME_SLOTS = [
  { key: "morning",   label: "Morning",   icon: Sun },
  { key: "afternoon", label: "Afternoon", icon: Sunset },
  { key: "evening",   label: "Evening",   icon: Moon },
];

const Toggle = ({ checked, onChange, disabled = false, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    data-testid={testid}
    className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${
      disabled ? "opacity-40 cursor-not-allowed" : ""
    } ${checked ? "brand-gradient-bg" : "bg-[#D8D4C7]"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const DisableConfirmModal = ({ open, onCancel, onConfirm }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md flex items-end sm:items-center justify-center px-4"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="notif-disable-modal"
          className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
        >
          <button
            onClick={onCancel}
            data-testid="notif-disable-modal-close"
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-[#FBE2DA] flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-[#9C3E1D]" />
          </div>
          <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-4">
            Are you sure you want to turn off notifications?
          </h2>
          <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
            Turning off notifications puts you at <strong>real risk of eating spoiled food</strong>.
            You won't get past best-by warnings, daily overdue reminders, or any of the alerts you've configured —
            which kind of defeats the point of using QFresh in the first place.
          </p>
          <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
            We strongly recommend leaving alerts on. You can always silence individual thresholds instead.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              data-testid="notif-disable-cancel"
              className="rounded-2xl bg-[#277A82] hover:bg-[#1F5566] text-white font-semibold py-3 transition-colors"
            >
              Keep alerts on
            </button>
            <button
              type="button"
              onClick={onConfirm}
              data-testid="notif-disable-confirm"
              className="rounded-2xl border-2 border-[#9C3E1D] text-[#9C3E1D] hover:bg-[#FBEAE5] font-semibold py-3 transition-colors"
            >
              Turn off anyway
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/**
 * LastPreExpiryConfirmModal — shown when the user tries to turn OFF the LAST
 * pre-expiry threshold (24h / 48h / 72h / 96h). Without at least one of these
 * enabled, the user gets zero advance warning before food goes bad — the
 * whole reason QFresh exists. We make them confirm explicitly.
 */
const LastPreExpiryConfirmModal = ({ open, rowLabel, onCancel, onConfirm }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onCancel}
        className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md flex items-end sm:items-center justify-center px-4"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="notif-last-preexpiry-modal"
          className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
        >
          <button
            onClick={onCancel}
            data-testid="notif-last-preexpiry-close"
            aria-label="Close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-[#FBE2DA] flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-[#9C3E1D]" />
          </div>
          <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-4">
            Turn off your last pre-expiry notice?
          </h2>
          <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
            <strong>{rowLabel}</strong> is the only pre-expiry alert you have switched on.
            If you turn it off, you won't get <strong>any</strong> warning before your
            food crosses its best-by date — which is the whole reason QFresh exists.
          </p>
          <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
            We strongly recommend keeping at least one pre-expiry notice on.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onCancel}
              data-testid="notif-last-preexpiry-cancel"
              className="rounded-2xl bg-[#277A82] hover:bg-[#1F5566] text-white font-semibold py-3 transition-colors"
            >
              Keep it on
            </button>
            <button
              type="button"
              onClick={onConfirm}
              data-testid="notif-last-preexpiry-confirm"
              className="rounded-2xl border-2 border-[#9C3E1D] text-[#9C3E1D] hover:bg-[#FBEAE5] font-semibold py-3 transition-colors"
            >
              Turn it off
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { user, updateMe } = useAuth();

  const initial = useMemo(
    () => ({ ...DEFAULT_PREFS, ...(user?.notification_prefs || {}) }),
    [user?.notification_prefs]
  );
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await notificationsApi.testEmail();
      toast.success(`Test email sent to ${res.delivered_to}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setTesting(false);
    }
  };

  const persist = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      await updateMe({ notification_prefs: next });
    } catch (err) {
      toast.error(formatError(err));
      setPrefs(initial);
    } finally {
      setSaving(false);
    }
  };

  const toggleKey = (key, v) => persist({ ...prefs, [key]: v });

  // Guard: if the user tries to turn OFF the LAST enabled pre-expiry threshold
  // (24h/48h/72h/96h), warn them they'll lose every pre-expiry email. This is
  // the whole point of the app — silent failure here means rotting food.
  const expireKeys = EXPIRATION_ROWS.map((r) => r.key);
  const [lastPreExpiryWarn, setLastPreExpiryWarn] = useState(null); // null | "expire_24h" etc.
  const requestExpiryToggle = (key, v) => {
    if (v) { toggleKey(key, true); return; }
    const enabledCount = expireKeys.filter((k) => prefs[k]).length;
    if (enabledCount <= 1 && prefs[key]) {
      setLastPreExpiryWarn(key);
      return;
    }
    toggleKey(key, false);
  };
  const confirmLastPreExpiryOff = () => {
    if (lastPreExpiryWarn) toggleKey(lastPreExpiryWarn, false);
    setLastPreExpiryWarn(null);
  };

  const masterOff = !prefs.enabled;

  const requestDisable = () => setDisableModalOpen(true);
  const confirmDisable = () => {
    setDisableModalOpen(false);
    persist({ ...prefs, enabled: false });
  };
  const reEnable = () => persist({ ...prefs, enabled: true });

  const toggleCustomDay = (dayKey) => {
    const set = new Set(prefs.custom_reminder_days || []);
    if (set.has(dayKey)) set.delete(dayKey); else set.add(dayKey);
    persist({ ...prefs, custom_reminder_days: Array.from(set).sort((a, b) => a - b) });
  };

  const toggleCustomTime = (slot) => {
    const set = new Set(prefs.custom_reminder_times || []);
    if (set.has(slot)) set.delete(slot); else set.add(slot);
    persist({ ...prefs, custom_reminder_times: Array.from(set) });
  };

  return (
    <div className="px-5 pt-5 pb-28">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="notif-back-btn"
          className="w-9 h-9 rounded-full bg-white border border-[#E2E0D8] flex items-center justify-center text-[#4F5D75] hover:bg-[#F5F3ED] transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">Settings</div>
          <h1 className="font-heading text-2xl font-bold text-[#2D3142] leading-tight">
            Notifications
          </h1>
        </div>
      </div>

      {/* Pre-expiry windows */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="mt-5"
        data-testid="notif-expire-section"
      >
        <div className="flex items-center gap-2 mb-2 px-1">
          <Clock className="w-3.5 h-3.5 text-[#8A93A6]" />
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
            Pre-expiry notices
          </div>
        </div>
        <div className="tup-card divide-y divide-[#E2E0D8] overflow-hidden">
          {EXPIRATION_ROWS.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-4 px-5 py-4"
              data-testid={`notif-row-${row.key}`}
            >
              <div className="min-w-0">
                <div className="font-semibold text-sm text-[#2D3142]">{row.label}</div>
                <div className="text-xs text-[#8A93A6] mt-0.5">{row.hint}</div>
              </div>
              <Toggle
                checked={prefs[row.key]}
                onChange={(v) => requestExpiryToggle(row.key, v)}
                disabled={masterOff}
                testid={`notif-toggle-${row.key}`}
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* Custom reminder */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="mt-5"
        data-testid="notif-custom-section"
      >
        <div className="flex items-center gap-2 mb-2 px-1">
          <Bell className="w-3.5 h-3.5 text-[#8A93A6]" />
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
            Daily prompt
          </div>
        </div>
        <div className="tup-card overflow-hidden">
          <div className="flex items-start justify-between gap-4 p-5">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-[#2D3142]">Custom reminder</div>
              <p className="text-xs text-[#8A93A6] mt-1 leading-relaxed">
                A friendly nudge to ask if you're storing anything today.
              </p>
            </div>
            <Toggle
              checked={prefs.custom_reminder}
              onChange={(v) => toggleKey("custom_reminder", v)}
              disabled={masterOff}
              testid="notif-toggle-custom"
            />
          </div>

          <AnimatePresence initial={false}>
            {prefs.custom_reminder && !masterOff && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="border-t border-[#E2E0D8] bg-[#F5F3ED]/50"
                data-testid="notif-custom-config"
              >
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                    When would you like to receive reminders?
                  </div>

                  {/* Day-of-week picker */}
                  <div
                    className="mt-3 flex gap-1.5"
                    data-testid="notif-custom-days"
                  >
                    {WEEKDAYS.map((d) => {
                      const active = (prefs.custom_reminder_days || []).includes(d.key);
                      return (
                        <button
                          key={d.key}
                          type="button"
                          onClick={() => toggleCustomDay(d.key)}
                          data-testid={`notif-custom-day-${d.key}`}
                          aria-pressed={active}
                          className={`flex-1 rounded-xl py-2 text-[11px] font-bold uppercase tracking-wider transition-all ${
                            active
                              ? "brand-gradient-bg text-white shadow-md"
                              : "bg-white border border-[#E2E0D8] text-[#4F5D75] hover:border-[#277A82]/60"
                          }`}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Time-of-day picker */}
                  <div className="mt-5 text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                    Time of day
                  </div>
                  <div
                    className="mt-2 grid grid-cols-3 gap-2"
                    data-testid="notif-custom-times"
                  >
                    {TIME_SLOTS.map((slot) => {
                      const Icon = slot.icon;
                      const active = (prefs.custom_reminder_times || []).includes(slot.key);
                      return (
                        <button
                          key={slot.key}
                          type="button"
                          onClick={() => toggleCustomTime(slot.key)}
                          data-testid={`notif-custom-time-${slot.key}`}
                          aria-pressed={active}
                          className={`flex flex-col items-center justify-center gap-1 py-3 rounded-2xl border-2 transition-all ${
                            active
                              ? "border-[#277A82] bg-[#DCEDF0] text-[#1F5566]"
                              : "border-[#E2E0D8] bg-white text-[#4F5D75] hover:border-[#277A82]/60"
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${active ? "text-[#1F5566]" : "text-[#8A93A6]"}`} />
                          <span className="text-xs font-semibold">{slot.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <div
        className="mt-5 text-center text-[11px] text-[#8A93A6]"
        data-testid="notif-save-status"
      >
        {saving ? "Saving…" : "Changes save automatically"}
      </div>

      {/* Test email — verify Resend delivery end-to-end */}
      <button
        type="button"
        onClick={sendTest}
        disabled={testing || masterOff}
        data-testid="notif-send-test-email-btn"
        className="mt-6 w-full flex items-center justify-center gap-2 bg-white border-2 border-[#277A82] text-[#1F5566] font-semibold rounded-2xl py-3 hover:bg-[#DCEDF0] disabled:opacity-50 transition-colors"
      >
        <Send className="w-4 h-4" />
        {testing ? "Sending…" : "Send me a test email"}
      </button>
      <p className="text-[11px] text-[#8A93A6] mt-2 px-1 leading-relaxed text-center">
        Confirms email alerts are actually reaching your inbox.
      </p>

      {/* Phone push notifications — per-device toggle */}
      <PushEnableCard disabled={masterOff} />

      {/* Always-on past-due reminder banner — sits down here near the
          master switch so users see the "we never go fully silent" note
          right next to the only control that can override it. */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-8 tup-card p-4 border-2 border-[#E07A5F]/40 bg-[#FBE9DD]"
        data-testid="notif-pastdue-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D95D39] flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-base font-semibold text-[#8C2A0F]">
              Past best-by reminders are always on
            </div>
            <p className="text-xs text-[#8C2A0F]/85 mt-1 leading-relaxed">
              We'll email you once a day for every container that's past its best-by date —
              for your safety. The only way to silence these is to turn off all notifications
              below.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Master switch — last item on the page, behind a disclaimer */}
      <div
        className="mt-10 border-t border-[#E2E0D8] pt-6"
        data-testid="notif-master-section"
      >
        <div className="mt-3 tup-card p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  prefs.enabled ? "brand-gradient-bg" : "bg-[#F5F3ED]"
                }`}
              >
                {prefs.enabled ? (
                  <Bell className="w-5 h-5 text-white" />
                ) : (
                  <BellOff className="w-5 h-5 text-[#8A93A6]" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-heading text-base font-semibold text-[#2D3142]">
                  All notifications
                </div>
                <div className="text-xs text-[#8A93A6] mt-0.5">
                  Master switch — silences everything, including past-due.
                </div>
              </div>
            </div>
            <Toggle
              checked={prefs.enabled}
              onChange={(v) => (v ? reEnable() : requestDisable())}
              testid="notif-master-toggle"
            />
          </div>
        </div>
        <p className="text-[11px] text-[#9C3E1D] mt-2 px-1 leading-relaxed">
          Turning this off removes ALL alerts — even daily past-best-by warnings.
          We strongly recommend leaving it on.
        </p>
      </div>

      <DisableConfirmModal
        open={disableModalOpen}
        onCancel={() => setDisableModalOpen(false)}
        onConfirm={confirmDisable}
      />

      <LastPreExpiryConfirmModal
        open={!!lastPreExpiryWarn}
        rowLabel={
          EXPIRATION_ROWS.find((r) => r.key === lastPreExpiryWarn)?.label || ""
        }
        onCancel={() => setLastPreExpiryWarn(null)}
        onConfirm={confirmLastPreExpiryOff}
      />
    </div>
  );
};

export default NotificationSettings;
